<script setup>
import { ref, computed } from 'vue'

// Props
const props = defineProps({
  questions: {
    type: Array,
    required: true
  },
  generations: {
    type: Array,
    required: true
  },
  csrfToken: {
    type: String,
    required: true
  },
  device: {
    type: String,
    default: 'pc'
  },
  previousResult: {
    type: Object,
    default: null
  }
})

// State
const currentScreen = ref('start')
const currentQuestion = ref(1)
const answers = ref({})
const gender = ref(null)
const generation = ref(null)
const isSubmitting = ref(false)
const copyButtonText = ref('コピー')

// Gender options
const genders = [
  { value: 'male', label: '男性' },
  { value: 'female', label: '女性' }
]

// Computed
const totalQuestions = computed(() => props.questions.length)
const progress = computed(() => (currentQuestion.value / totalQuestions.value) * 100)
const currentQuestionData = computed(() => props.questions[currentQuestion.value - 1])
const canSubmit = computed(() => gender.value !== null && generation.value !== null)

// Methods
function showScreen(screen) {
  currentScreen.value = screen
}

function startDiagnosis() {
  showScreen('question')
}

function selectAnswer(answer) {
  answers.value[`q${currentQuestion.value}`] = answer

  setTimeout(() => {
    if (currentQuestion.value < totalQuestions.value) {
      currentQuestion.value++
    } else {
      showScreen('generation')
    }
  }, 300)
}

function goBack() {
  if (currentScreen.value === 'generation') {
    currentQuestion.value = totalQuestions.value
    showScreen('question')
  } else if (currentQuestion.value > 1) {
    currentQuestion.value--
  }
}

function skipQuestion() {
  const randomAnswers = ['a', 'b', 'c', 'd']
  const randomAnswer = randomAnswers[Math.floor(Math.random() * randomAnswers.length)]
  selectAnswer(randomAnswer)
}

function selectGender(gen) {
  gender.value = gen
  checkAndSubmit()
}

function selectGeneration(gen) {
  generation.value = gen
  checkAndSubmit()
}

function checkAndSubmit() {
  if (gender.value !== null && generation.value !== null) {
    setTimeout(() => {
      submitResults()
    }, 300)
  }
}

async function submitResults() {
  showScreen('submitting')
  isSubmitting.value = true

  const formData = new FormData()
  Object.entries(answers.value).forEach(([key, value]) => {
    formData.append(`answers[${key}]`, value)
  })
  formData.append('gender', gender.value)
  formData.append('generation', generation.value)

  try {
    const response = await fetch('/shindan/result', {
      method: 'POST',
      headers: {
        'X-CSRF-Token': props.csrfToken
      },
      credentials: 'same-origin',
      body: formData
    })

    if (response.redirected) {
      window.location.href = response.url
    } else if (response.ok) {
      window.location.href = response.url || '/shindan'
    } else {
      throw new Error('Submit failed')
    }
  } catch (error) {
    console.error('Error:', error)
    alert('送信に失敗しました。もう一度お試しください。')
    showScreen('generation')
  } finally {
    isSubmitting.value = false
  }
}

function isAnswerSelected(answer) {
  return answers.value[`q${currentQuestion.value}`] === answer
}

// Share functions
function getTwitterShareUrl() {
  if (!props.previousResult) return '#'
  const shareText = `趣味診断の結果は「${props.previousResult.typeName}」\nオススメの趣味は「${props.previousResult.article?.title}」でした！\n#趣味サーチ`
  return `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(props.previousResult.url)}`
}

function getLineShareUrl() {
  if (!props.previousResult) return '#'
  return `https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(props.previousResult.url)}`
}

function copyToClipboard() {
  if (!props.previousResult) return
  const input = document.getElementById(`share-url-input-${props.previousResult.token}`)
  if (input) {
    input.select()
    input.setSelectionRange(0, 99999)
    document.execCommand('copy')
    copyButtonText.value = props.device === 'pc' ? 'コピーしました！' : 'OK'
    setTimeout(() => {
      copyButtonText.value = 'コピー'
    }, 2000)
  }
}
</script>

<template>
  <div class="shindan-container">
    <!-- スタート画面 -->
    <Transition name="fade">
      <div v-if="currentScreen === 'start'" class="shindan-screen">
        <!-- タイトルはサーバー描画の h1（pc/shindan/index.html.erb）で出力 -->
        <div class="shindan-content">
          <img v-if="device === 'pc'" :src="'/img/pc/shindan/start.png'" alt="趣味診断イメージ" class="shindan-image" />
          <img v-if="device === 'sp'" :src="'/img/sp/shindan/start.png'" alt="趣味診断イメージ" class="shindan-image" />
          <p class="shindan-headline-lead">趣味がない、趣味を探している、新しい趣味を始めたいという方に最適な無料の趣味診断です。8つの質問に答えるだけで、あなたの性格や生活スタイルを分析し、ぴったりの趣味をご提案します。</p>
          <div class="shindan-info">
            <div class="info-item">
              <span class="info-icon">Q</span>
              <span class="info-text">全{{ totalQuestions }}問</span>
            </div>
          </div>
          <button class="shindan-btn primary" @click="startDiagnosis">診断スタート</button>

          <!-- 前回の結果がある場合 -->
          <div v-if="previousResult" class="previous-result-section">
            <div class="previous-result-header">
              <p class="previous-result-label">ー 前回の診断結果 ー</p>
              <h2 class="previous-result-type-name">{{ previousResult.typeName }}</h2>
              <div class="previous-result-type-detail" :class="{ 'has-image': previousResult.typeImage }">
                <div v-if="previousResult.typeImage" class="previous-result-type-image">
                  <img :src="previousResult.typeImage" :alt="previousResult.typeName" />
                </div>
                <p class="previous-result-type-description">{{ previousResult.typeDescription }}</p>
              </div>
            </div>

            <div v-if="previousResult.article" class="previous-result-content">
              <p class="previous-result-recommend-label">そんなあなたにオススメの趣味は...</p>
              <div class="hobby_block previous-result-hobby-block" :class="{ 'cf': device === 'sp' }">
                <div class="inner previous-result-inner">
                  <a :href="previousResult.article.url">
                    <p class="cat" :class="`cat_${previousResult.article.categoryCode}`">
                      <img :src="previousResult.article.categoryIconUrl" :alt="previousResult.article.categoryName" />
                      {{ previousResult.article.categoryName }}
                    </p>
                    <div class="previous-result-image-wrapper">
                      <img :src="previousResult.article.imageUrl" :alt="previousResult.article.title" />
                    </div>
                    <div class="txt">
                      <p class="ttl">{{ previousResult.article.title }}</p>
                      <p>{{ previousResult.article.lead }}</p>
                    </div>
                  </a>
                </div>
              </div>
            </div>

            <!-- シェアボタン -->
            <div class="result-share">
              <h3>結果をシェアしよう！</h3>
              <div class="share-buttons">
                <a :href="getTwitterShareUrl()" target="_blank" rel="noopener" class="share-btn twitter">
                  {{ device === 'pc' ? 'X (Twitter)' : 'X' }}
                </a>
                <a :href="getLineShareUrl()" target="_blank" rel="noopener" class="share-btn line">
                  LINE
                </a>
              </div>
              <div class="share-url">
                <input type="text" :value="previousResult.url" readonly :id="`share-url-input-${previousResult.token}`">
                <button type="button" class="copy-btn" @click="copyToClipboard">
                  {{ copyButtonText }}
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>
    </Transition>

    <!-- 質問画面 -->
    <Transition name="fade">
      <div v-if="currentScreen === 'question'" class="shindan-screen">
        <div class="shindan-progress">
          <div class="progress-bar-container">
            <div class="progress-bar" :style="{ width: progress + '%' }"></div>
          </div>
          <span class="progress-text">{{ currentQuestion }} / {{ totalQuestions }}</span>
        </div>
        <div class="shindan-content">
          <div class="question-header">
            <span class="question-number">Q{{ currentQuestion }}</span>
          </div>
          <h2 class="question-text">{{ currentQuestionData.text }}</h2>
          <div class="options-container">
            <div
              v-for="(value, key) in currentQuestionData.options"
              :key="key"
              class="shindan-option"
              :class="{ selected: isAnswerSelected(key) }"
              @click="selectAnswer(key)"
            >
              <span class="option-label">{{ key }}</span>
              <span class="option-text">{{ value }}</span>
            </div>
          </div>
          <div class="question-actions">
            <button
              v-if="currentQuestion > 1"
              class="shindan-btn secondary"
              @click="goBack"
            >戻る</button>
            <button class="shindan-btn secondary" @click="skipQuestion">スキップ</button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- 性別・年代選択画面 -->
    <Transition name="fade">
      <div v-if="currentScreen === 'generation'" class="shindan-screen">
        <div class="shindan-content">
          <h2 class="generation-title">性別を選択してください</h2>
          <div class="gender-options">
            <div
              v-for="g in genders"
              :key="g.value"
              class="shindan-gender"
              :class="{ selected: gender === g.value }"
              @click="selectGender(g.value)"
            >
              {{ g.label }}
            </div>
          </div>

          <h2 class="generation-title">年代を選択してください</h2>
          <div class="generation-options">
            <div
              v-for="gen in generations"
              :key="gen.value"
              class="shindan-generation"
              :class="{ selected: generation === gen.value }"
              @click="selectGeneration(gen.value)"
            >
              {{ gen.label }}
            </div>
          </div>
          <div class="question-actions">
            <button class="shindan-btn secondary" @click="goBack">戻る</button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- 送信中画面 -->
    <Transition name="fade">
      <div v-if="currentScreen === 'submitting'" class="shindan-screen">
        <div class="shindan-content">
          <div class="loading-spinner"></div>
          <p class="loading-text">あなたにピッタリの趣味を分析中...</p>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
